/**
 * Provider Jobs Service
 * Service layer for DDD provider-jobs API endpoints
 *
 * This service calls the provider-jobs endpoints which use the
 * DDD-compliant backend system with proper domain aggregates.
 */

import { apiClient } from './api';
import type {
  ProviderJobDto,
  CreateJobInput,
  JobFilters,
  RescheduleJobInput,
  ResourceType,
  BatchCreateResponse,
  JobResponse,
  JobListResponse,
  BatchCreateJobResponse,
} from '@/types/provider-job';

/**
 * Get authentication token from localStorage
 */
function getToken(): string | null {
  return localStorage.getItem('sp_auth_token');
}

/**
 * Provider Jobs Service
 */
export const providerJobsService = {
  /**
   * List jobs with optional filters
   */
  async listJobs(filters?: JobFilters): Promise<ProviderJobDto[]> {
    const token = getToken();
    const queryParams = new URLSearchParams();

    if (filters?.status) {
      queryParams.append('status', filters.status);
    }
    if (filters?.fromDate) {
      queryParams.append('fromDate', filters.fromDate);
    }
    if (filters?.toDate) {
      queryParams.append('toDate', filters.toDate);
    }
    if (filters?.customerId) {
      queryParams.append('customerId', filters.customerId);
    }

    const url = `/provider/jobs${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await apiClient.get<JobListResponse>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.jobs;
  },

  /**
   * Get single job by ID
   */
  async getJob(jobId: string): Promise<ProviderJobDto> {
    const token = getToken();

    const response = await apiClient.get<JobResponse>(
      `/provider/jobs/${jobId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.job;
  },

  /**
   * Create a new job manually
   */
  async createJob(input: CreateJobInput): Promise<ProviderJobDto> {
    const token = getToken();

    const response = await apiClient.post<JobResponse>(
      '/provider/jobs',
      input,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.job;
  },

  /**
   * Create multiple jobs in batch
   */
  async createJobsBatch(jobs: CreateJobInput[]): Promise<BatchCreateResponse> {
    const token = getToken();

    const response = await apiClient.post<BatchCreateJobResponse>(
      '/provider/jobs/batch',
      { jobs },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.result;
  },

  /**
   * Start a job
   * Transitions job from 'scheduled' to 'in_progress'
   * Requires all necessary resources to be assigned
   */
  async startJob(jobId: string): Promise<ProviderJobDto> {
    const token = getToken();

    const response = await apiClient.post<JobResponse>(
      `/provider/jobs/${jobId}/start`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.job;
  },

  /**
   * Complete a job
   * Transitions job from 'in_progress' to 'completed'
   */
  async completeJob(
    jobId: string,
    completionNotes?: string
  ): Promise<ProviderJobDto> {
    const token = getToken();

    const response = await apiClient.post<JobResponse>(
      `/provider/jobs/${jobId}/complete`,
      { completionNotes },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.job;
  },

  /**
   * Cancel a job
   * Transitions job to 'cancelled' and releases all resources
   */
  async cancelJob(jobId: string, reason: string): Promise<ProviderJobDto> {
    const token = getToken();

    const response = await apiClient.post<JobResponse>(
      `/provider/jobs/${jobId}/cancel`,
      { reason },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.job;
  },

  /**
   * Reschedule a job
   * Updates the scheduled date and time window
   * Only allowed for 'scheduled' jobs
   */
  async rescheduleJob(
    jobId: string,
    schedule: RescheduleJobInput
  ): Promise<ProviderJobDto> {
    const token = getToken();

    const response = await apiClient.patch<JobResponse>(
      `/provider/jobs/${jobId}/schedule`,
      schedule,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.job;
  },

  /**
   * Assign resources to a job
   * Can assign container, vehicle, and/or driver in a single call
   * Only allowed for 'scheduled' jobs
   */
  async assignResources(
    jobId: string,
    resources: {
      containerId?: string;
      vehicleId?: string;
      driverId?: string;
    }
  ): Promise<ProviderJobDto> {
    const token = getToken();

    const response = await apiClient.post<JobResponse>(
      `/provider/jobs/${jobId}/assign-resources`,
      resources,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.job;
  },

  /**
   * Unassign a resource from a job
   * Removes container, vehicle, or driver assignment
   * Only allowed for 'scheduled' jobs
   */
  async unassignResource(
    jobId: string,
    type: ResourceType
  ): Promise<ProviderJobDto> {
    const token = getToken();

    const response = await apiClient.delete<JobResponse>(
      `/provider/jobs/${jobId}/resources/${type}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.job;
  },

  /**
   * Delete a job
   * Only allowed for 'scheduled' or 'cancelled' jobs
   */
  async deleteJob(jobId: string): Promise<void> {
    const token = getToken();

    await apiClient.delete(`/provider/jobs/${jobId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  /**
   * Update custom field values on a job
   * Validates fields against job type definition
   */
  async updateJobCustomFields(
    jobId: string,
    customFields: Record<string, unknown>
  ): Promise<ProviderJobDto> {
    const token = getToken();

    const response = await apiClient.patch<JobResponse>(
      `/provider/jobs/${jobId}/custom-fields`,
      { customFields },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.job;
  },
};
